import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft, ChevronDown, MessageCircle, Video, Phone,
  Search, Code, Rocket, FileText, Save, Loader2,
  Cloud, CloudOff,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { voiceAgentAPI, voiceAIAPI } from '../../services/api';
import { SaveStatusIndicator } from './SaveStatusIndicator';
import { AgentTabsNav } from './AgentTabsNav';
import { TestAgentModal } from './TestAgentModal';

const AgentHeader = ({ 
  agent, 
  setAgent, 
  activeTab, 
  setActiveTab,
  viewMode,
  setViewMode,
  showTestDropdown,
  setShowTestDropdown,
  showDeployDropdown,
  setShowDeployDropdown,
  onSave,
  saving = false,
  saveStatus = 'saved',
  lastSaved = null,
  autoSaveEnabled = true,
  setAutoSaveEnabled = () => {}
}) => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const isNewAgent = agentId === 'new';
  
  // Test modal state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testMode, setTestMode] = useState(null); // 'chat', 'web', 'phone'
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [testingCall, setTestingCall] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  
  // Web call state
  const [webCallActive, setWebCallActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [webCallTranscript, setWebCallTranscript] = useState([]);
  const [currentUserSpeech, setCurrentUserSpeech] = useState('');
  const [webCallSessionId, setWebCallSessionId] = useState(null); // Persistent session ID
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const currentSpeechRef = useRef('');
  const webCallActiveRef = useRef(false);
  const isAISpeakingRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    currentSpeechRef.current = currentUserSpeech;
  }, [currentUserSpeech]);

  useEffect(() => {
    webCallActiveRef.current = webCallActive;
  }, [webCallActive]);

  useEffect(() => {
    isAISpeakingRef.current = isAISpeaking;
  }, [isAISpeaking]);

  // Handle test button clicks
  const handleTestChat = () => {
    setTestMode('chat');
    setShowTestModal(true);
    setShowTestDropdown(false);
    setChatMessages([
      { role: 'assistant', content: agent?.welcome_message || 'Hello! How can I help you today?' }
    ]);
  };

  const handleTestWebCall = () => {
    setTestMode('web');
    setShowTestModal(true);
    setShowTestDropdown(false);
    setTestMessage('Click "Start Call" to begin a voice conversation with your AI agent.');
    setWebCallTranscript([]);
    setWebCallActive(false);
  };

  // Counter for unique session IDs across web calls (deterministic across renders)
  const sessionCounterRef = useRef(0);
  const webCallSessionIdRef = useRef(null); // Stable session id across speech-recognition closures

  // Start web call
  const startWebCall = async () => {
    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      setWebCallActive(true);
      webCallActiveRef.current = true;
      setWebCallTranscript([]);
      setTestMessage('');
      
      // Create a unique session ID for this call (counter-based, deterministic)
      sessionCounterRef.current += 1;
      const newSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? `webcall-${agent?.id || 'test'}-${crypto.randomUUID()}`
        : `webcall-${agent?.id || 'test'}-${sessionCounterRef.current}`;
      setWebCallSessionId(newSessionId);
      webCallSessionIdRef.current = newSessionId;
      
      // Play welcome message - listening will start automatically when audio ends
      const welcomeMsg = agent?.welcome_message || 'Hello! How can I help you today?';
      setWebCallTranscript([{ role: 'ai', content: welcomeMsg }]);
      await playAIResponse(welcomeMsg);
      
      // Note: Don't call startListening() here - it's called in playAIResponse.onended
    } catch (error) {
      console.error('Microphone access error:', error);
      setTestMessage('❌ Microphone access denied. Please allow microphone access and try again.');
    }
  };

  // End web call
  const endWebCall = () => {
    setWebCallActive(false);
    setIsListening(false);
    setIsAISpeaking(false);
    setCurrentUserSpeech('');
    setWebCallSessionId(null); // Reset session ID when call ends
    webCallSessionIdRef.current = null;
    
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    setTestMessage('Call ended. Click "Start Call" to begin a new conversation.');
  };

  // Play AI response using TTS
  const playAIResponse = async (text) => {
    // IMMEDIATELY stop any speech recognition when AI starts speaking
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      } catch (e) {
        // Ignore errors
      }
    }
    setIsListening(false);
    setIsAISpeaking(true);
    isAISpeakingRef.current = true;
    
    try {
      const response = await voiceAIAPI.preview(
        agent?.voiceProvider || 'openai',
        agent?.voiceId || 'alloy',
        text
      );
      
      if (response.status === 200) {
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        
        const audio = new Audio(url);
        audioRef.current = audio;
        
        audio.onended = () => {
          console.log('Audio ended, resetting speaking state');
          setIsAISpeaking(false);
          isAISpeakingRef.current = false;
          URL.revokeObjectURL(url);
          // Start listening immediately after audio ends (minimal delay)
          if (webCallActiveRef.current) {
            setTimeout(() => {
              console.log('Starting listening after audio end');
              if (webCallActiveRef.current && !isAISpeakingRef.current) {
                startListening();
              }
            }, 200);
          }
        };
        
        audio.onerror = (e) => {
          console.error('Audio playback error:', e);
          setIsAISpeaking(false);
          isAISpeakingRef.current = false;
          URL.revokeObjectURL(url);
          if (webCallActiveRef.current) {
            setTimeout(() => {
              if (webCallActiveRef.current && !isAISpeakingRef.current) {
                startListening();
              }
            }, 200);
          }
        };
        
        await audio.play();
      } else {
        console.log('TTS response not ok, resetting speaking state');
        setIsAISpeaking(false);
        isAISpeakingRef.current = false;
        if (webCallActiveRef.current) {
          setTimeout(() => {
            if (webCallActiveRef.current && !isAISpeakingRef.current) {
              startListening();
            }
          }, 300);
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsAISpeaking(false);
      isAISpeakingRef.current = false;
      if (webCallActiveRef.current) {
        setTimeout(() => {
          if (webCallActiveRef.current && !isAISpeakingRef.current) {
            startListening();
          }
        }, 300);
      }
    }
  };

  // Start speech recognition
  const startListening = () => {
    // CRITICAL: Don't start listening if AI is speaking (to prevent echo)
    if (isAISpeakingRef.current || isAISpeaking) {
      console.log('AI is speaking, cannot start listening');
      return;
    }
    
    // Don't start if not in active web call
    if (!webCallActiveRef.current) {
      console.log('Web call not active, cannot start listening');
      return;
    }
    
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setTestMessage('❌ Speech recognition not supported in this browser. Try Chrome.');
      return;
    }

    // Stop any existing recognition first
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      } catch (e) {
        // Ignore errors when stopping
      }
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setCurrentUserSpeech('');
      currentSpeechRef.current = '';
    };

    recognition.onresult = (event) => {
      // Abort immediately if AI started speaking
      if (isAISpeakingRef.current) {
        try {
          recognition.stop();
        } catch (e) { /* recognition already stopped */ }
        return;
      }
      
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setCurrentUserSpeech(transcript);
      currentSpeechRef.current = transcript;
    };

    recognition.onend = () => {
      setIsListening(false);
      
      // Don't process if AI started speaking (echo prevention)
      if (isAISpeakingRef.current) {
        console.log('AI speaking, discarding captured speech');
        setCurrentUserSpeech('');
        currentSpeechRef.current = '';
        return;
      }
      
      const userSpeech = currentSpeechRef.current.trim();
      const userSpeechLower = userSpeech.toLowerCase();

      // Get the last AI message to check for echo (compare against full last AI message)
      const lastAIMessage = webCallTranscript.filter(t => t.role === 'ai').pop()?.content?.toLowerCase().trim() || '';

      // Echo detection (precise, not over-eager):
      // Only flag as echo if the captured speech is *substantially* the same as the last AI message
      // (i.e. the user transcript is contained inside the AI's last message AND is non-trivial).
      // Single/short words are legitimate user answers (e.g. "yes", "8 PM", "tomorrow", "John").
      let isLikelyEcho = false;
      if (userSpeechLower && lastAIMessage && userSpeechLower.length >= 8) {
        // Must be a substring of the AI's last spoken text to count as echo
        if (lastAIMessage.includes(userSpeechLower)) {
          // And must cover a meaningful chunk of the AI message (>= 40%)
          const ratio = userSpeechLower.length / lastAIMessage.length;
          if (ratio >= 0.4) {
            isLikelyEcho = true;
          }
        }
      }
      
      if (userSpeech && webCallActiveRef.current && !isLikelyEcho) {
        // Add user speech to transcript
        setWebCallTranscript(prev => [...prev, { role: 'user', content: userSpeech }]);
        setCurrentUserSpeech('');
        currentSpeechRef.current = '';
        
        // Get AI response
        getAIResponse(userSpeech);
      } else if (userSpeech) {
        console.log('Filtered out likely echo:', userSpeech);
        setCurrentUserSpeech('');
        currentSpeechRef.current = '';
        
        // Restart listening after a short delay
        if (webCallActiveRef.current && !isAISpeakingRef.current) {
          setTimeout(() => {
            if (webCallActiveRef.current && !isAISpeakingRef.current) {
              startListening();
            }
          }, 300);
        }
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      
      if (event.error === 'no-speech' && webCallActiveRef.current) {
        // Restart listening if no speech detected
        setTimeout(() => {
          if (webCallActiveRef.current && !isAISpeaking) {
            startListening();
          }
        }, 500);
      }
    };

    recognition.start();
  };

  // Get AI response using chat API
  const getAIResponse = async (userMessage) => {
    try {
      // Build agent script from conversational flow sections.
      // Support BOTH camelCase (conversationalFlow) and snake_case (conversational_flow)
      // field names since the API/DB persistence mixes both conventions.
      let agentScript = '';
      const flow = agent?.conversationalFlow || agent?.conversational_flow || [];
      if (Array.isArray(flow) && flow.length > 0) {
        agentScript = flow
          .filter((f) => f.enabled !== false && (f.content || f.title))
          .map((f, i) => {
            const title = (f.title || `Step ${i + 1}`).trim();
            const content = (f.content || '').trim();
            return content ? `Step ${i + 1} (${title}): ${content}` : `Step ${i + 1}: ${title}`;
          })
          .join('\n');
        if (agentScript) {
          agentScript =
            `You are ${agent?.name || 'an AI voice agent'} on a phone call. ` +
            `Follow the script below in order, one question at a time. ` +
            `Do NOT repeat earlier questions once answered. ` +
            `Keep replies short (1-2 sentences).\n\n${agentScript}`;
        }
      }
      if (!agentScript) {
        agentScript = agent?.script || agent?.welcome_message || agent?.welcomeMessage || '';
      }

      const response = await voiceAIAPI.chat(
        userMessage,
        webCallSessionIdRef.current || webCallSessionId,
        agentScript,
        agent?.name || 'AI Assistant'
      );
      
      if (response.data?.success) {
        const aiResponse = response.data.response || "I understand. Is there anything else I can help you with?";
        setWebCallTranscript(prev => [...prev, { role: 'ai', content: aiResponse }]);
        await playAIResponse(aiResponse);
      } else {
        const fallback = "I apologize, could you please repeat that?";
        setWebCallTranscript(prev => [...prev, { role: 'ai', content: fallback }]);
        await playAIResponse(fallback);
      }
    } catch (error) {
      console.error('AI response error:', error);
      const fallback = "I'm sorry, there was a connection issue. Please try again.";
      setWebCallTranscript(prev => [...prev, { role: 'ai', content: fallback }]);
      try {
        await playAIResponse(fallback);
      } catch (ttsError) {
        // If even fallback TTS fails, recover gracefully
        console.error('Fallback TTS also failed:', ttsError);
        setIsAISpeaking(false);
        isAISpeakingRef.current = false;
        if (webCallActiveRef.current) {
          setTimeout(() => {
            if (webCallActiveRef.current && !isAISpeakingRef.current) {
              startListening();
            }
          }, 500);
        }
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const handleTestPhoneCall = async () => {
    setTestMode('phone');
    setShowTestModal(true);
    setShowTestDropdown(false);
    setTestMessage('');
  };

  const initiateTestCall = async () => {
    if (!testPhoneNumber) {
      setTestMessage('Please enter a phone number');
      return;
    }
    
    setTestingCall(true);
    setTestMessage('');
    
    try {
      // Call the test endpoint
      const response = await voiceAgentAPI.testCall(agent.id, testPhoneNumber);
      if (response.data.success) {
        setTestMessage(`✅ Test call initiated! You should receive a call at ${testPhoneNumber} shortly.`);
      } else {
        setTestMessage(`❌ Failed to initiate call: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Test call error:', error);
      setTestMessage(`❌ Error: ${error.response?.data?.detail || error.message || 'Failed to initiate test call'}`);
    } finally {
      setTestingCall(false);
    }
  };

  const sendChatMessage = () => {
    if (!chatInput.trim()) return;
    
    // Add user message
    setChatMessages(prev => [...prev, { role: 'user', content: chatInput }]);
    
    // Simulate AI response (in real implementation, this would call the API)
    setTimeout(() => {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'This is a simulated response. In production, this would be connected to your AI agent.' 
      }]);
    }, 1000);
    
    setChatInput('');
  };

  // Get save status display
  const saveStatusNode = (
    <SaveStatusIndicator
      saveStatus={saveStatus}
      lastSaved={lastSaved}
      isNewAgent={isNewAgent}
    />
  );

  return (
    <>
      {/* Top Navigation Bar */}
      <div className="bg-[#0d1117] border-b border-gray-800 px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Left Side */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/client/agents')}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
            
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={agent.name}
                onChange={(e) => setAgent(prev => ({ ...prev, name: e.target.value }))}
                className="text-lg font-semibold text-white bg-transparent border-none outline-none focus:ring-2 focus:ring-teal-400/50 rounded px-2 py-1"
                data-testid="agent-name-input"
              />
              
              <div className="relative">
                <button className="flex items-center gap-1 px-3 py-1.5 bg-[#1a1a1f] border border-gray-700 rounded-lg text-sm text-gray-300">
                  <span className={agent.type === 'outgoing' ? 'text-blue-400' : 'text-green-400'}>
                    {agent.type === 'outgoing' ? 'Outgoing' : 'Incoming'}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              
              <span className="text-gray-500 text-sm">
                Cost/min: <span className="text-teal-400">${agent.costPerMin}</span>
              </span>
              
              {/* Save Status Indicator */}
              {saveStatusNode}
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Auto-save Toggle - Only for existing agents */}
            {!isNewAgent && (
              <button
                onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  autoSaveEnabled 
                    ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' 
                    : 'bg-gray-800 text-gray-500 border border-gray-700'
                }`}
                title={autoSaveEnabled ? 'Auto-save is ON' : 'Auto-save is OFF'}
                data-testid="auto-save-toggle"
              >
                {autoSaveEnabled ? (
                  <Cloud className="w-3.5 h-3.5" />
                ) : (
                  <CloudOff className="w-3.5 h-3.5" />
                )}
                Auto-save {autoSaveEnabled ? 'ON' : 'OFF'}
              </button>
            )}
            {/* Test Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowTestDropdown(!showTestDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1f] border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-[#252530] transition-colors"
                data-testid="test-dropdown-btn"
              >
                Test with
                <ChevronDown className="w-4 h-4" />
              </button>
              {showTestDropdown && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-[#1a1a1f] border border-gray-700 rounded-lg shadow-xl z-20">
                  <button 
                    onClick={handleTestChat}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-800 transition-colors rounded-t-lg"
                    data-testid="test-chat-btn"
                  >
                    <MessageCircle className="w-5 h-5 text-teal-400" />
                    Chat
                  </button>
                  <button 
                    onClick={handleTestWebCall}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-800 transition-colors"
                    data-testid="test-web-btn"
                  >
                    <Video className="w-5 h-5 text-blue-400" />
                    Web Call
                  </button>
                  <button 
                    onClick={handleTestPhoneCall}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-800 transition-colors rounded-b-lg"
                    data-testid="test-phone-btn"
                  >
                    <Phone className="w-5 h-5 text-green-400" />
                    Phone Call
                  </button>
                </div>
              )}
            </div>

            {/* Search */}
            <div className="relative hidden lg:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search or jump to..."
                className="w-48 bg-[#1a1a1f] border border-gray-700 rounded-lg pl-10 pr-12 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-teal-400"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded">⌘K</span>
            </div>

            {/* UI/Code Toggle */}
            <div className="flex bg-[#1a1a1f] border border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('ui')}
                className={`px-3 py-2 text-sm transition-colors ${viewMode === 'ui' ? 'bg-teal-400 text-black' : 'text-gray-400'}`}
              >
                UI
              </button>
              <button
                onClick={() => setViewMode('code')}
                className={`px-3 py-2 text-sm transition-colors ${viewMode === 'code' ? 'bg-teal-400 text-black' : 'text-gray-400'}`}
              >
                <Code className="w-4 h-4" />
              </button>
            </div>

            {/* Save Button - Always Visible */}
            <button
              onClick={onSave}
              disabled={saving || !agent.name}
              className="flex items-center gap-2 px-5 py-2 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 disabled:cursor-not-allowed text-black rounded-lg font-medium transition-colors"
              data-testid="save-agent-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isNewAgent ? 'Create Agent' : 'Save Changes'}
                </>
              )}
            </button>

            {/* Deploy Button - For existing agents only */}
            {!isNewAgent && (
              <div className="relative">
                <button
                  onClick={() => setShowDeployDropdown(!showDeployDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                  data-testid="deploy-btn"
                >
                  <Rocket className="w-4 h-4" />
                  Deploy
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showDeployDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-[#1a1a1f] border border-gray-700 rounded-lg shadow-xl z-20">
                    <button 
                      onClick={onSave}
                      className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-800 transition-colors"
                    >
                      <Rocket className="w-5 h-5 text-green-400" />
                      Deploy to Production
                    </button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-800 transition-colors">
                      <FileText className="w-5 h-5 text-blue-400" />
                      Save as Draft
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Navigation Tabs */}
      <AgentTabsNav activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* Test Modal */}
      {showTestModal && (
        <TestAgentModal
          testMode={testMode}
          onClose={() => {
            setShowTestModal(false);
            setTestMode(null);
            setTestMessage('');
            setTestPhoneNumber('');
          }}
          chatMessages={chatMessages}
          chatInput={chatInput}
          onChatInputChange={setChatInput}
          onSendMessage={sendChatMessage}
          webCallActive={webCallActive}
          isListening={isListening}
          isAISpeaking={isAISpeaking}
          webCallTranscript={webCallTranscript}
          currentUserSpeech={currentUserSpeech}
          testMessage={testMessage}
          onStartWebCall={startWebCall}
          onEndWebCall={endWebCall}
          testPhoneNumber={testPhoneNumber}
          setTestPhoneNumber={setTestPhoneNumber}
          testingCall={testingCall}
          onInitiateCall={initiateTestCall}
        />
      )}
    </>
  );
};

export default AgentHeader;
